import { closeCurrentModal } from 'redux/slices/modals/modals.slice';
import { EReduxQueryTags } from 'shared/types/api.interface';
import { IChallangeData, ICreateChallengeRequest } from 'shared/types/challenges.interface';
import { IDebateData } from 'shared/types/debates.interface';
import { queryUrls } from 'shared/constants/queryApiUrl-constants';
import { ERRORS } from 'shared/constants/validation-constants';
import { handleSnackbarNotify } from 'utils/handleSnackbarNotify';
import { handleCatchNotValidationError } from 'utils/handleCatchNotValidationError';
import { CHALLENGES_FETCHES_SUCCESS } from 'shared/constants/alert-constants';
import { splitApi } from './split.api';

export const ChallengeApi = splitApi.injectEndpoints({
  endpoints: (build) => ({
    getAllChallenges: build.query<IChallangeData[], void>({
      query: () => ({
        url: queryUrls.challengeApi.getAllChallenges,
      }),
      providesTags: [EReduxQueryTags.Challenge],
      keepUnusedDataFor: 35,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleSnackbarNotify(dispatch, ERRORS.tryUpdatePage, 'error');
        }
      },
    }),
    getMyChallenges: build.query<IChallangeData[], void>({
      query: () => ({
        url: queryUrls.challengeApi.getMyChallenges,
      }),
      providesTags: [EReduxQueryTags.Challenge],
      keepUnusedDataFor: 35,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleSnackbarNotify(dispatch, ERRORS.tryUpdatePage, 'error');
        }
      },
    }),
    createChallenge: build.mutation<IChallangeData, ICreateChallengeRequest>({
      query: (body) => ({ url: queryUrls.challengeApi.createChallenge, method: 'POST', body }),
      invalidatesTags: () => [EReduxQueryTags.Challenge],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(closeCurrentModal());
          handleSnackbarNotify(dispatch, CHALLENGES_FETCHES_SUCCESS.createChallenge, 'success');
        } catch (err) {
          handleCatchNotValidationError(err, dispatch);
        }
      },
    }),
    setOpponent: build.mutation<IChallangeData, number>({
      query: (id) => ({
        url: `${queryUrls.challengeApi.getAllChallenges}${id}${queryUrls.challengeApi.setOpponent}`,
        method: 'PATCH',
      }),
      invalidatesTags: () => [EReduxQueryTags.Challenge, EReduxQueryTags.Debate],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          handleSnackbarNotify(dispatch, CHALLENGES_FETCHES_SUCCESS.setOpponent, 'success');
        } catch (err) {
          handleCatchNotValidationError(err, dispatch);
        }
      },
    }),
    cancelChallenge: build.mutation<IDebateData, number>({
      query: (id) => ({
        url: `${queryUrls.challengeApi.getAllChallenges}${id}${queryUrls.challengeApi.cancelChallenge}`,
        method: 'PATCH',
      }),
      invalidatesTags: () => [EReduxQueryTags.Challenge, EReduxQueryTags.Debate],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          handleSnackbarNotify(dispatch, CHALLENGES_FETCHES_SUCCESS.cancelChallenge, 'success');
        } catch (err) {
          handleCatchNotValidationError(err, dispatch);
        }
      },
    }),
    getCurrentChallenge: build.query<IChallangeData, string | number>({
      query: (id) => ({
        url: `${queryUrls.challengeApi.getAllChallenges}${id}/`,
      }),
      providesTags: [EReduxQueryTags.CurrentChallenge],
      keepUnusedDataFor: 35,
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleCatchNotValidationError(err, dispatch);
        }
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllChallengesQuery,
  useGetMyChallengesQuery,
  useCreateChallengeMutation,
  useSetOpponentMutation,
  useCancelChallengeMutation,
  useGetCurrentChallengeQuery,
} = ChallengeApi;
